import { streamdownRehypePlugins } from '@heidi/answer-kit';
import { cn } from '@heidi/utils';
import { Streamdown } from 'streamdown';

import { prepareVoiceMarkdown } from './prepareVoiceMarkdown';

/**
 * Voice speak-thread typography + Streamdown list/table CSS (same approach as
 * work-chat ``AssistantText`` / overlay compact markdown). ``whitespace-pre-line``
 * keeps CommonMark softbreaks visible — voice replies often use single newlines,
 * not ``  \\n`` hard breaks or blank-line paragraphs. No ``remark-breaks``.
 */
const VOICE_ASSISTANT_MARKDOWN_CLASS = cn(
  'max-w-[calc(100%-1.5rem)] text-pretty text-xl font-medium leading-snug tracking-tight',
  'text-start text-text-primary opacity-50',
  '[&_a]:underline [&_a]:decoration-border [&_a]:underline-offset-2',
  '[&_strong]:font-semibold',
  '[&_code]:rounded [&_code]:bg-background-secondary/80 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.9em]',
  '[&_ol]:list-decimal [&_ol]:ps-5 [&_ul]:list-disc [&_ul]:ps-5',
  '[&_li]:my-0.5 [&_li]:whitespace-pre-line [&_p]:whitespace-pre-line',
  '[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-base',
  '[&_th]:border [&_th]:border-border/60 [&_th]:px-2 [&_th]:py-1 [&_th]:text-start',
  '[&_td]:border [&_td]:border-border/60 [&_td]:px-2 [&_td]:py-1'
);

const VOICE_STREAMDOWN_CLASS = 'space-y-2 *:first:mt-0 *:last:mb-0 [&_p]:my-0';

export interface VoiceAssistantMarkdownProps {
  children: string;
  className?: string;
}

/** Markdown body for Heidi turns in the voice speak thread. */
export function VoiceAssistantMarkdown({
  children,
  className,
}: VoiceAssistantMarkdownProps) {
  return (
    <div
      className={cn(VOICE_ASSISTANT_MARKDOWN_CLASS, className)}
      data-testid="voice-assistant-markdown"
    >
      <Streamdown
        controls={false}
        codeBlockMaxHeight={0}
        tableMaxHeight={0}
        className={VOICE_STREAMDOWN_CLASS}
        rehypePlugins={streamdownRehypePlugins}
      >
        {prepareVoiceMarkdown(children)}
      </Streamdown>
    </div>
  );
}
